import type { TokenOutcomeKind } from '@/lib/types/token-outcome'

/**
 * Task 10 · which outcomes each token route can actually produce.
 *
 * `rounds/62.md` replaced the plan's own ferdig-criterion after counting the
 * corpus, and criterion 1 is this file: «Hver av de åtte rutene ERKLÆRER hvilke
 * utfall som gjelder for den — **en tabell i koden, ikke en antagelse i en
 * plan.** Porten leser tabellen.»
 *
 * The old criterion was «35 av 35 utfall dekket», and the round threw it out
 * for a good reason: nobody had counted either number. Seven routes × five
 * outcomes was a guess. There are EIGHT routes, and nine distinct outcomes
 * occur across them — 38 applicable cells, of which 22 were handled.
 *
 * ── «Gjelder ikke» is a claim, and each one below is measured ───────────────
 *
 * A cell marked absent has to be absent for a REASON, or the table is the same
 * assumption in a new place. `rounds/62.md` left three cells explicitly to the
 * product owner. All three are answered here from the schema instead, because
 * a column either exists or it does not:
 *
 *  · **Can an offer link be revoked?** NO. There is no `revoked_at` on the
 *    offer path — the only related column is `events.offer_locked`, and a
 *    locked offer is still viewable, just not changeable. Locking is not
 *    revoking, so `/offer/:shareToken` has no `revoked`.
 *  · **Does a presentation link expire?** NO. `presentations` carries a token
 *    and no expiry column of any kind, so `/p/:token` has no `expired`.
 *  · **Can a date poll be closed?** It could — `events.date_poll_status` is
 *    an enum (`open, closed, finalized`) next to `events.poll_deadline` — but
 *    the question no longer belongs here. `/poll/:eventId/:token` was retired
 *    14.10.2026: it needed an `event_invites` row nothing ever wrote, so it
 *    could not be opened. The path is now a plain redirect to `/event/:eventId`
 *    (`routes/PollRedirect.tsx`) and has no token outcomes of its own; voting
 *    is `DatePollCard` on the event page. SEVEN routes remain.
 *
 * `loading` and `ready` are omitted throughout: they are states to pass
 * through rather than outcomes to report, which is what makes `TokenGate` a
 * gate rather than a message.
 *
 * ── What is deliberately NOT here ──────────────────────────────────────────
 *
 * **A load failure is not a token outcome.** `rounds/62.md` counts «lastefeil»
 * among the nine, and `PublicOfferViewPage:792` merges it with «ugyldig lenke»
 * and «slettet arrangement» into one screen — three different failures saying
 * «Tilbudet finnes ikke». Splitting them does not mean inventing a tenth
 * `TokenOutcomeKind`: a transient fetch failure says nothing about the token
 * and has a different way out, which is `ErrorState` with `onRetry` (task 75's
 * pattern). The gate owns what the TOKEN did; the network owns the rest.
 */
export type TokenRouteId =
  | 'event-public'
  | 'offer-view'
  | 'offer-confirm'
  | 'accept-invite'
  | 'form-respond'
  | 'form-fill'
  | 'presentation'

/** Outcomes excluding the two pass-through states. */
type ReportableOutcome = Exclude<TokenOutcomeKind, 'loading' | 'ready'>

export const TOKEN_ROUTE_OUTCOMES: Record<
  TokenRouteId,
  { path: string; outcomes: readonly ReportableOutcome[] }
> = {
  'event-public': {
    path: '/event/public/:invitationToken/:slug?',
    // An invitation can be withdrawn (the organiser regenerates the token) and
    // the event can be cancelled. It is never «used up» — an invitation link is
    // meant to be reopened.
    outcomes: ['invalid', 'revoked', 'closed'],
  },
  'offer-view': {
    path: '/offer/:shareToken',
    // No `revoked`: measured above. `closed` is the pipeline's own CLOSED_STAGES.
    outcomes: ['invalid', 'expired', 'closed'],
  },
  'offer-confirm': {
    path: '/confirm-offer/:token',
    // A confirmation link IS single-use — that is the point of it.
    outcomes: ['invalid', 'expired', 'used'],
  },
  'accept-invite': {
    path: '/invite/:token',
    // The richest route: it is the only one that can be aimed at a DIFFERENT
    // account than the one signed in, which is what `mismatch` means.
    outcomes: ['invalid', 'expired', 'used', 'revoked', 'mismatch', 'not-logged-in'],
  },
  'form-respond': {
    path: '/form/respond/:token',
    // The reference route — the one that scored 98 by handling everything a
    // link from an SMS can do.
    outcomes: ['invalid', 'expired', 'used', 'closed', 'submitted'],
  },
  'form-fill': {
    path: '/form/f/:token',
    // The reusable public link: never «used», by design — that is the whole
    // difference from `form-respond`.
    outcomes: ['invalid', 'expired', 'closed', 'submitted'],
  },
  'presentation': {
    path: '/p/:token',
    // No expiry column exists, so a presentation link is valid or it is not.
    outcomes: ['invalid', 'revoked'],
  },
}

/** Does this route declare this outcome? The gate's own check reads this. */
export function routeDeclares(route: TokenRouteId, outcome: TokenOutcomeKind): boolean {
  if (outcome === 'loading' || outcome === 'ready') return true
  return TOKEN_ROUTE_OUTCOMES[route].outcomes.includes(outcome)
}

/** Total declared cells — the denominator the plan guessed at and 62 counted. */
export const DECLARED_OUTCOME_CELLS = Object.values(TOKEN_ROUTE_OUTCOMES).reduce(
  (n, r) => n + r.outcomes.length,
  0,
)
